"use client";

import { MantineProvider } from "@mantine/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type PropsWithChildren } from "react";

import { mantineTheme, themeVariablesResolver } from "@/app/mantine-theme";
import { ClientConfig } from "@/components/client-config";
import { ExtensionProvider } from "@/components/extension-provider";
import { GrowthBookContextProvider } from "@/context/growthbook.context";
import { SegmentContextProvider } from "@/context/segment.context";
import { UserContextProvider } from "@/context/user.context";
import { defaultShouldRetry } from "@/utils/react-query";

// Module-level so the cache persists across re-renders — do not move inside the component.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: defaultShouldRetry,
      retryOnMount: false,
      // 15 minutes
      staleTime: 15 * 60 * 1000,
    },
  },
});

export const Providers = ({ children }: PropsWithChildren) => {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider
        cssVariablesResolver={themeVariablesResolver}
        theme={mantineTheme}
      >
        <UserContextProvider>
          <SegmentContextProvider>
            <GrowthBookContextProvider>
              <ClientConfig />
              {/* Legacy Chrome extension integration — not actively maintained */}
              <ExtensionProvider>{children}</ExtensionProvider>
            </GrowthBookContextProvider>
          </SegmentContextProvider>
        </UserContextProvider>
      </MantineProvider>
    </QueryClientProvider>
  );
};
